import { Field } from '@base-ui/react/field';
import { Fieldset } from '@base-ui/react/fieldset';
import { NumberField } from '@/components/ui/NumberField';
import {
  ScheduleActivityAxis,
  ScheduleActivityBar,
} from '@/components/ui/ScheduleActivityBar';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { US_TIMEZONE_OPTIONS } from '@/consts/settings';
import { useScheduleSettings } from '@/providers/settings/SettingsContext';

const formatHourLabel = (hour: number) => {
  if (hour === 0) {
    return '12am';
  }

  if (hour === 12) {
    return '12pm';
  }

  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, hour) => ({
  label: formatHourLabel(hour),
  value: String(hour),
}));

export const ScheduleSection = () => {
  const {
    disableWeekendScans,
    mark,
    nextScanMin,
    offpeakMin,
    peakEndHour,
    peakMin,
    peakStartHour,
    setDisableWeekendScans,
    setOffpeakMin,
    setPeakEndHour,
    setPeakMin,
    setPeakStartHour,
    setTimezone,
    timezone,
  } = useScheduleSettings();

  return (
    <Fieldset.Root>
      <Fieldset.Legend render={<SectionHeader>Schedule</SectionHeader>} />
      <div className="space-y-4 rounded-lg border border-border bg-surface/60 p-4">
        <div className="grid grid-cols-[1fr_auto_1fr_1fr] items-end gap-3">
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Peak starts
              </Field.Label>
            </div>
            <Select
              className="w-full"
              items={HOUR_OPTIONS}
              onValueChange={(value) => mark(setPeakStartHour)(Number(value))}
              value={String(peakStartHour)}
            />
          </Field.Root>
          <span className="pb-2 font-mono text-xs lg:text-sm text-subtle">
            <span>to</span>
          </span>
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Peak ends
              </Field.Label>
            </div>
            <Select
              className="w-full"
              items={HOUR_OPTIONS}
              onValueChange={(value) => mark(setPeakEndHour)(Number(value))}
              value={String(peakEndHour)}
            />
          </Field.Root>
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Timezone
              </Field.Label>
            </div>
            <Select
              className="w-full"
              items={US_TIMEZONE_OPTIONS}
              onValueChange={mark(setTimezone)}
              value={timezone}
            />
          </Field.Root>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Peak scan interval (minutes)
              </Field.Label>
              <Field.Description className="font-mono text-xs lg:text-sm text-subtle">
                {formatHourLabel(peakStartHour)} –{' '}
                {formatHourLabel(peakEndHour)}
              </Field.Description>
            </div>
            <NumberField
              font="mono"
              min={1}
              onValueChange={(v) => mark(setPeakMin)(v ?? 0)}
              size="sm"
              step={1}
              value={peakMin}
            />
          </Field.Root>
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Off-peak scan interval (minutes)
              </Field.Label>
              <Field.Description className="font-mono text-xs lg:text-sm text-subtle">
                {formatHourLabel(peakEndHour)} –{' '}
                {formatHourLabel(peakStartHour)}
              </Field.Description>
            </div>
            <NumberField
              font="mono"
              min={1}
              onValueChange={(v) => mark(setOffpeakMin)(v ?? 0)}
              size="sm"
              step={1}
              value={offpeakMin}
            />
          </Field.Root>
        </div>
        <Field.Root className="flex items-center justify-between">
          <Field.Label className="text-sm lg:text-base font-medium text-foreground">
            Pause scans on weekends
          </Field.Label>
          <Switch
            checked={disableWeekendScans}
            label="Toggle weekend scan pause"
            onCheckedChange={mark(setDisableWeekendScans)}
          />
        </Field.Root>
        <div>
          <p className="mb-1.5 font-mono text-xs lg:text-sm tracking-wider text-subtle uppercase">
            24-hour activity
          </p>
          <ScheduleActivityBar
            offpeakMin={offpeakMin}
            peakEndHour={peakEndHour}
            peakMin={peakMin}
            peakStartHour={peakStartHour}
            timezone={timezone}
          />
          <ScheduleActivityAxis />
          <p className="mt-2 font-mono text-xs lg:text-sm text-subtle">
            Peak scans every <span className="text-foreground">{peakMin}m</span>
            , off-peak every{' '}
            <span className="text-foreground">{offpeakMin}m</span>.
            {nextScanMin != null && (
              <>
                {' '}
                Next scan in{' '}
                <span className="text-foreground">{nextScanMin}m</span>.
              </>
            )}
          </p>
        </div>
      </div>
    </Fieldset.Root>
  );
};
